import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { IdCard, Monitor, ShieldCheck, Smartphone, Tablet } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * "One system, built right" — elevates the system's accuracy, security, and
 * cross-device reach (points previously buried in the experienced-advisor
 * block) into a prominent section beneath the demo. Ivory band, continuing the
 * light "platform" chapter before the dark economics section.
 */
export default function PlatformTrust() {
    const shouldReduceMotion = useReducedMotion();

    return (
        <section className="agent-px border-t border-[#0e2727]/10 bg-[#f7f4ec] py-20 sm:py-28">
            <div className="mx-auto max-w-5xl">
                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="max-w-3xl"
                >
                    <span className="lux-caps lux-caps-lg mb-5 block text-[#0e2727]/50">One System, Built Right</span>
                    {/* Editorial pull-quote treatment — italic Didot + large faint hanging
                        opening quote, mirroring .editorial-prose blockquote in the articles. */}
                    <blockquote className="relative">
                        {/* Quotation mark hangs into the left margin so the quote text + attribution
                            stay flush-left with the kicker and the pillars below. */}
                        <span
                            aria-hidden="true"
                            className="lux-didot pointer-events-none absolute top-0 -left-4 text-[4.5rem] leading-none text-[#0e2727]/10 select-none sm:-left-12 sm:text-[6rem]"
                        >
                            &ldquo;
                        </span>
                        <p className="lux-didot lux-didot-lg text-[#0e2727] italic">
                            The speed of an OTA. The margins of a tour operator. The control of an independent advisor.
                        </p>
                        <cite className="lux-caps lux-caps-sm mt-6 block text-[#0e2727]/50 not-italic">
                            — Chris Cardena, VP of Business Development
                        </cite>
                    </blockquote>
                </motion.div>

                <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-3">
                    {/* 1 — CRM, no re-keying */}
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <IdCard className="mb-4 h-6 w-6 text-[#0e2727]/60" strokeWidth={1.5} />
                        <h3 className="lux-body lux-body-sm mb-2 font-medium text-[#0e2727]/90">Tied to your CRM — nothing re-keyed</h3>
                        <p className="lux-body lux-body-sm leading-relaxed text-[#0e2727]/60">
                            Client rewards numbers, passport details, and payment info flow straight from the client profile into every booking. No
                            retyping &mdash; and none of the transcription errors that can turn one wrong passport digit into a five-figure liability.
                        </p>
                    </motion.div>

                    {/* 2 — Security */}
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    >
                        <ShieldCheck className="mb-4 h-6 w-6 text-[#0e2727]/60" strokeWidth={1.5} />
                        <h3 className="lux-body lux-body-sm mb-2 font-medium text-[#0e2727]/90">Defense-grade, PCI-compliant</h3>
                        <p className="lux-body lux-body-sm leading-relaxed text-[#0e2727]/60">
                            Our CRM and FastBook were personally built and coded by our founder, who came from cybersecurity for defense contractors.
                            3DS card verification (where the client&rsquo;s bank supports it) and structured profiles protect every transaction.
                        </p>
                    </motion.div>

                    {/* 3 — Every device */}
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                    >
                        <div className="mb-4 flex items-center gap-2 text-[#0e2727]/60">
                            <Monitor className="h-6 w-6" strokeWidth={1.5} />
                            <Tablet className="h-5 w-5" strokeWidth={1.5} />
                            <Smartphone className="h-4 w-4" strokeWidth={1.5} />
                        </div>
                        <h3 className="lux-body lux-body-sm mb-2 font-medium text-[#0e2727]/90">One platform, every device</h3>
                        <p className="lux-body lux-body-sm leading-relaxed text-[#0e2727]/60">
                            Search, book, invoice, collect payment, and track commissions in one place &mdash; fully featured on desktop, tablet, and
                            phone. Sell from anywhere, not just your desk. Most legacy systems still can&rsquo;t say that.
                        </p>
                    </motion.div>
                </div>

                <motion.p
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
                    className="lux-caps lux-caps-sm mt-16 border-t border-[#0e2727]/10 pt-8 leading-relaxed text-[#0e2727]/45"
                >
                    Travel Leaders Network — the largest luxury network in North America · Preferred partner programs include Rosewood, Four Seasons,
                    Mandarin Oriental, Belmond,{' '}
                    <Link
                        href="/benefits"
                        className="text-[#0e2727]/70 underline underline-offset-4 transition-colors hover:text-[#0e2727]"
                    >
                        and many more
                    </Link>
                </motion.p>
            </div>
        </section>
    );
}
